import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_RAIL_WIDTH } from './Sidebar';
import MobileBottomNav from './MobileBottomNav';
import MobileHeader from './MobileHeader';
import { AnimatedSidebar } from './AnimatedComponents';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import { useNavigationMemory } from '../hooks/useNavigationMemory';
import { T, SPRING_PANEL } from '../theme/radar';

interface LayoutProps {
  children: React.ReactNode;
}

const COLLAPSED_KEY = 'bymarke_sidebar_collapsed';

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  useKeyboardNavigation(true);
  // Tasto indietro e refresh riportano l'utente dove era (scroll compreso)
  useNavigationMemory();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Rail a sole icone (desktop). Come nel Radar il puntatore lo riapre
  // temporaneamente e il contenuto si sposta con lui.
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [peeking, setPeeking] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* storage non disponibile: resta solo per la sessione */
      }
      return next;
    });
  }, []);

  // Close sidebar on route change or escape key
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && sidebarOpen) {
        closeSidebar();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen, closeSidebar]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  const desktopSidebarWidth = collapsed && !peeking ? SIDEBAR_RAIL_WIDTH : SIDEBAR_WIDTH;

  return (
    <div
      className="flex min-h-screen overflow-x-hidden"
      style={{ backgroundColor: T.bg }}
    >
      {/* Mobile Header */}
      <MobileHeader onMenuToggle={toggleSidebar} isSidebarOpen={sidebarOpen} />

      {/* Mobile Sidebar Overlay — sopra header (z-50) e bottom nav (9997) */}
      <div
        className={`
          fixed inset-0 z-[9998] lg:hidden
          transition-opacity duration-300 ease-in-out
          ${sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      >
        {/* Backdrop: velo d'inchiostro come lo scrim del Drawer */}
        <div
          className={`
            absolute inset-0
            transition-opacity duration-300
            ${sidebarOpen ? 'opacity-100' : 'opacity-0'}
          `}
          style={{ backgroundColor: 'rgba(19,22,28,0.32)' }}
          onClick={closeSidebar}
          aria-hidden="true"
        />

        {/* Sidebar Drawer */}
        <div
          className={`
            absolute left-0 top-0 h-full w-[280px] sm:w-[300px]
            transform transition-transform duration-300 ease-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <Sidebar onNavigate={closeSidebar} isMobile={true} />
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="desktop-sidebar hidden lg:block fixed left-0 top-0 h-full z-40">
        <AnimatedSidebar>
          <Sidebar
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
            onPeekChange={setPeeking}
          />
        </AnimatedSidebar>
      </div>

      {/* Main content area — il margine segue la larghezza del menù con la
          stessa molla, così contenuto e rail si muovono insieme */}
      <motion.div
        className="flex-1 flex flex-col min-h-0 overflow-x-hidden max-w-full main-content-shell"
        initial={false}
        animate={{ '--sidebar-w': `${desktopSidebarWidth}px` } as Record<string, string>}
        transition={SPRING_PANEL}
      >
        {/* Main Content */}
        <main
          id="main-content"
          className="flex-1 min-h-0 overflow-x-hidden"
          role="main"
          tabIndex={-1}
        >
          {/* Responsive padding:
              - Mobile: pt-16 (header height) + px-3, pb-20 (bottom nav)
              - sm: px-4
              - md: px-5
              - lg: pt-0 (no header), px-6, pb-6
              - xl: px-8
          */}
          <div className="
            p-3 pt-[72px] pb-24
            sm:p-4 sm:pt-[72px] sm:pb-24
            md:p-5 md:pt-[72px] md:pb-24
            lg:p-6 lg:pt-6 lg:pb-6
            xl:p-8 xl:pt-8 xl:pb-8
            transition-all duration-300
            overflow-x-hidden max-w-full
          ">
            {children}
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        <div
          className="lg:hidden"
          id="navigation"
          role="navigation"
          aria-label="Mobile navigation"
        >
          <MobileBottomNav />
        </div>
      </motion.div>
    </div>
  );
};

export default Layout;
